import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import withAuth from '../../HOC/withAuth';
import http from '../../http';
import C from '../../colors';
import { paymentStatusMeta } from '../../orderStatus';

function BackIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  );
}

function Kpi({ label, value, hint, accent, testId }) {
  return (
    <div data-testid={testId} style={{
      background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 10,
      padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.ink3, fontWeight: 600 }}>
        {label}
      </div>
      <div data-testid={testId ? `${testId}-value` : undefined} style={{
        fontFamily: C.serif, fontSize: 32, lineHeight: 1,
        color: accent ? C.brand2 : C.ink,
        fontVariantNumeric: 'tabular-nums', marginTop: 2,
      }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function StatBars({ title, data, labels, testId }) {
  const entries = Object.entries(data || {});
  const total = entries.reduce((s, [, v]) => s + v, 0);
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, padding: '18px 20px' }}>
      <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.ink3, fontWeight: 600, marginBottom: 14 }}>
        {title}
      </div>
      {entries.length === 0 ? (
        <div style={{ fontSize: 13, color: C.ink3, padding: '12px 0' }}>暂无数据</div>
      ) : (
        [...entries].sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <div key={k} data-testid={testId ? `${testId}-${k}` : undefined} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span title={k} style={{ fontSize: 13, color: C.ink2, fontFamily: C.sans }}>
                {labels?.[k] || labels?.__fallback || '其他'}
              </span>
              <span style={{ fontSize: 13, color: C.ink3, fontFamily: C.mono, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
            </div>
            <div style={{ height: 4, background: C.hair, borderRadius: 2 }}>
              <div style={{
                height: '100%',
                width: `${total ? Math.round(v / total * 100) : 0}%`,
                background: C.brand, borderRadius: 2, transition: 'width .3s',
              }}/>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

const thStyle = (right) => ({
  fontFamily: C.mono, fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase',
  color: C.ink3, fontWeight: 600, paddingBottom: 8,
  borderBottom: `1px solid ${C.hair}`,
  textAlign: right ? 'right' : 'left',
});

const tdStyle = (right) => ({
  padding: '10px 0', borderBottom: `1px solid ${C.hair}`,
  fontFamily: C.sans, fontSize: 13, color: C.ink2,
  textAlign: right ? 'right' : 'left',
});

function SelectionRow({ sel }) {
  const paymentMeta = paymentStatusMeta(sel);
  return (
    <tr data-testid={`access-selection-${sel.id}`}>
      <td style={tdStyle()}>{sel.customerName || '—'}</td>
      <td style={{ ...tdStyle('center'), fontFamily: C.mono, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
        {sel.claimedPhotoCount}
      </td>
      <td style={{ ...tdStyle('center'), textAlign: 'center' }}>
        <span style={{ fontSize: 12, color: paymentMeta.tone === 'success' ? '#3F8F5F' : C.ink3 }}>
          {paymentMeta.label}
        </span>
      </td>
      <td style={{ ...tdStyle(), fontSize: 12, color: sel.remark ? C.ink2 : C.ink3 }}>
        {sel.remark || '—'}
      </td>
      <td style={{ ...tdStyle('right'), fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>
        {dayjs(sel.createdAt).format('MM-DD HH:mm')}
      </td>
    </tr>
  );
}

const EVENT_LABELS = {
  view_info: '查看订单信息',
  view_select: '浏览选片页',
  submit_selection: '提交选图',
  pay_requested: '发起支付',
  __fallback: '其他事件',
};

const CLIENT_LABELS = {
  desktop_browser: '桌面浏览器',
  mobile_browser: '手机浏览器',
  wechat_browser: '微信浏览器',
  wechat_miniprogram: '微信小程序',
  unknown: '未知设备',
  __fallback: '其他客户端',
};

function LogRow({ log }) {
  return (
    <tr
      data-testid={`access-log-${log.id}`}
      data-event-type={log.eventType}
      data-selection-id={log.selectionId || ''}
    >
      <td style={{ ...tdStyle(), fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>
        {dayjs(log.createdAt).format('MM-DD HH:mm')}
      </td>
      <td style={{ ...tdStyle(), fontSize: 12 }}>
        <span title={log.clientType || undefined}>
          {log.clientType ? (CLIENT_LABELS[log.clientType] || CLIENT_LABELS.__fallback) : '—'}
        </span>
      </td>
      <td style={{ ...tdStyle(), fontSize: 12, color: C.ink3 }}>
        <span title={log.eventType || undefined}>
          {log.eventType ? (EVENT_LABELS[log.eventType] || EVENT_LABELS.__fallback) : '—'}
        </span>
      </td>
    </tr>
  );
}

const AccessStatsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    http.get(`/user/order/${orderId}/accessStats`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { setError('加载失败'); setLoading(false); });
  }, [orderId]);

  if (loading) return (
    <div style={{ padding: '80px 40px', textAlign: 'center', color: C.ink3, fontFamily: C.sans, fontSize: 14 }}>
      加载中…
    </div>
  );

  if (error || !data) return (
    <div style={{ padding: '80px 40px', textAlign: 'center', color: C.ink3, fontFamily: C.sans, fontSize: 14 }}>
      {error || '加载失败'}
    </div>
  );

  const { order, summary, selectionSummary, latestAccessLogs } = data;
  return (
    <div style={{ padding: '32px 40px 60px', maxWidth: 960, margin: '0 auto' }}>
      <button onClick={() => navigate(-1)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12,
        background: 'none', border: 'none', cursor: 'pointer',
        color: C.ink3, fontFamily: C.sans, fontSize: 13, padding: 0,
      }}>
        <BackIcon/> 返回
      </button>

      <h1 style={{ margin: '0 0 28px', fontFamily: C.serif, fontSize: 38, fontWeight: 400, letterSpacing: -0.5, lineHeight: 1.1 }}>
        {order.customerName}
        <span style={{ color: C.ink3, fontSize: 18, marginLeft: 14, letterSpacing: 0 }}>· 访问统计</span>
      </h1>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        <Kpi testId="access-total-views" label="总访问量"    value={summary.totalViews}           hint="所有访问次数"/>
        <Kpi testId="access-unique-visitors" label="独立访客"    value={summary.uniqueVisitors}       hint="去重后"/>
        <Kpi testId="access-selected-visitors" label="已选图访客"  value={summary.selectedVisitorCount} hint="完成选图" accent/>
      </div>

      {/* Client + Event bars */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
        <StatBars title="客户端分布" data={summary.clientStats} labels={CLIENT_LABELS} testId="access-client-stat"/>
        <StatBars title="事件分布"   data={summary.eventStats} labels={EVENT_LABELS} testId="access-event-stat"/>
      </div>

      {/* Selection records */}
      <div style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, padding: '18px 20px', marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
          <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.ink3, fontWeight: 600 }}>
            选图记录
          </div>
          <div style={{ fontSize: 12, color: C.ink3 }}>
            共 {selectionSummary.totalSelections} 人 · {selectionSummary.totalSelectedPhotos} 张
          </div>
        </div>
        {selectionSummary.latestSelections.length === 0 ? (
          <div style={{ fontSize: 13, color: C.ink3, padding: '12px 0' }}>暂无选图记录</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle()}>客户</th>
                <th style={{ ...thStyle(), textAlign: 'center' }}>选图数</th>
                <th style={{ ...thStyle(), textAlign: 'center' }}>支付状态</th>
                <th style={thStyle()}>客户备注</th>
                <th style={thStyle('right')}>时间</th>
              </tr>
            </thead>
            <tbody>
              {selectionSummary.latestSelections.map(sel => <SelectionRow key={sel.id} sel={sel}/>)}
            </tbody>
          </table>
        )}
      </div>

      {/* Access logs */}
      <div style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, padding: '18px 20px' }}>
        <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.ink3, fontWeight: 600, marginBottom: 14 }}>
          最近访问记录
        </div>
        {latestAccessLogs.length === 0 ? (
          <div style={{ fontSize: 13, color: C.ink3, padding: '12px 0' }}>暂无访问日志</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle()}>时间</th>
                <th style={thStyle()}>设备</th>
                <th style={thStyle()}>事件</th>
              </tr>
            </thead>
            <tbody>
              {latestAccessLogs.map(log => <LogRow key={log.id} log={log}/>)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default withAuth(AccessStatsPage);
